import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { Samples } from '@df/samples/samples';
import { NSamples } from '@df/samples/samples.namespace';
import { IAddToBasketData } from '@df/session/api/basket/basket';

export class Sample {
    private _data: NSamples.ISampleData;

    constructor(
        public productId: number,
        private productResource: ProductResource,
        private localStorageService: LocalStorageService,
        private samples: Samples
    ) {
        this.data = this.localStorageService.get(this.storageName) || {};
    }

    /**
     * Sample id is exactly source product id
     */
    get id(): number {
        return this.productId;
    }

    /**
     * Data setter - syncs data object with local storage
     * @param  {ISampleData} data
     */
    set data(data: NSamples.ISampleData) {
        this.localStorageService.set(this.storageName, data || {});
        this._data = data || {};
    }

    /**
     * Data getter
     * @return {ISampleData} [description]
     */
    get data(): NSamples.ISampleData {
        return this._data;
    }

    private get storageName(): string {
        return `customerSample.${this.productId}`;
    }

    /**
     * Sampled product id
     * @return {Product}
     */
    get product(): Product<IProductDataV2> {
        return this.productResource.getCreate(this.productId) as Product<IProductDataV2>;
    }

    /**
     * Takes id of sample product
     */
    get sampleId(): number | undefined {
        return this.samples.getSampleId(this.product);
    }

    /**
     * Sample product model
     * @return {Product} [description]
     */
    get sampleProduct(): Product | undefined {
        const sampleId = this.sampleId;
        return sampleId ? this.productResource.getCreate(sampleId) : undefined;
    }

    /**
     * Loads product and sample
     */
    load(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.product
                .load(2)
                .then(() => {
                    const p = this.sampleProduct;
                    if (p) {
                        p.load(2).finally(resolve);
                    } else {
                        resolve();
                    }
                })
                .catch(reject);
        });
    }

    /**
     * Checks if samples has valid product loaded
     */
    hasValidProducts(): boolean {
        if (!(this.product && this.product.data)) {
            return false;
        }
        const sampleProduct = this.sampleProduct;
        if (sampleProduct && !sampleProduct.data) {
            return false;
        }
        return true;
    }

    /**
     * Removes this samples
     */
    remove(): void {
        this.samples.removeSampleFor(this.productId);
    }

    /**
     * Clear local storage on destroy
     */
    onDestroy() {
        this.localStorageService.set(this.storageName, null);
    }

    /**
     * Retrieves add to basket request
     * @return {number} [description]
     */
    toBasketRequest(): IAddToBasketData | undefined {
        const id = this.sampleId;
        if (id) {
            return { id: id, qty: 1 };
        }
        return undefined;
    }

    /**
     * Checks is sample is valid to add to basket
     * @return {boolean}
     */
    isValid(): boolean {
        const product = this.sampleProduct;
        return product && product.data ? true : false;
    }
}
